import React, { useEffect, useRef, useState } from "react";
import {
	Box,
	Button,
	Typography,
	Paper,
	TextField,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	IconButton,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { notify } from "../../reducers/NotifierReducer";
import { formatDate } from "../../utils/functions";
import {
	createVehicleNote,
	getVehicleNotes,
	updateVehicleNote,
} from "../../utils/commonServices";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";

// ─── Notes Tab
const NotesTab = ({ vehicle, classes }) => {
	const dispatch = useDispatch();

	const { userData, userAgency } = useSelector((state) => state.user);

	const [notes, setNotes] = useState([]);
	const [loading, setLoading] = useState(false);

	const [openModal, setOpenModal] = useState(false);
	const [noteText, setNoteText] = useState("");
	const [editNote, setEditNote] = useState(null);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

	const selectedNote = useRef(null);

	// Load Notes

	const loadNotes = async () => {
		if (!vehicle?.id) return;

		setLoading(true);
		try {
			const res = await getVehicleNotes(userAgency);

			setNotes(res || []);
		} catch (err) {
			dispatch(notify("Failed to load notes", "error"));
		}
		setLoading(false);
	};

	useEffect(() => {
		loadNotes();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vehicle?.id]);

	// Open Add

	const handleAdd = () => {
		setEditNote(null);
		setNoteText("");
		setOpenModal(true);
	};

	// Open Edit

	const handleEdit = (note) => {
		setEditNote(note);
		setNoteText(note.comment);
		setOpenModal(true);
	};

	// Save Note

	const handleSave = async () => {
		if (!noteText.trim()) return;

		try {
			if (editNote) {
				await updateVehicleNote(editNote.id, {
					comment: noteText,
					editedBy: userData.user.Username,
					editedAt: new Date(),
				});

				dispatch(notify("Note updated", "success"));
			} else {
				await createVehicleNote({
					vehicleId: vehicle.id,
					comment: noteText,
					createdBy: userData.user.Username,
					createdAt: new Date(),
					agencyId: userAgency,
					isDeleted: 0,
				});

				dispatch(notify("Note added", "success"));
			}

			setOpenModal(false);
			loadNotes();
		} catch (err) {
			dispatch(notify("Failed to save note", "error"));
		}
	};

	// Delete Note

	const handleDelete = async () => {
		const note = selectedNote.current;

		try {
			await updateVehicleNote(note.id, {
				isDeleted: 1,
				deletedBy: userData.user.ptsUserID,
				deletedDateTime: new Date(),
			});

			dispatch(notify("Note deleted", "success"));
			selectedNote.current = null;
			setOpenDeleteDialog(false);
			loadNotes();
		} catch (err) {
			dispatch(notify("Failed to delete note", "error"));
		}
	};

	return (
		<>
			<Box>
				{/* Add Button */}
				<div
					style={{
						display: "flex",
						justifyContent: "end",
						alignItems: "center",
					}}
				>
					<Button
						variant="outlined"
						sx={{ mb: 2 }}
						onClick={handleAdd}
					>
						+ Add Note
					</Button>
				</div>

				{/* Empty State */}
				{notes.length === 0 && !loading && (
					<Box className={classes.emptyState}>
						<Box className={classes.emptyIcon}>📝</Box>
						<Typography>
							No notes recorded for this vehicle yet
						</Typography>
					</Box>
				)}

				{/* Notes List */}
				{notes.map((note) => (
					<Paper
						key={note.id}
						sx={{
							p: 2,
							mb: 2,
							borderRadius: 2,
							cursor: "pointer",
						}}
						onClick={() => handleEdit(note)}
					>
						<Typography sx={{ mb: 1 }}>{note.comment}</Typography>

						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								fontSize: 12,
								color: "text.secondary",
							}}
						>
							<Box>
								<div>
									✍️ Created by <b>{note.createdBy}</b> —{" "}
									{formatDate(note.createdAt)}
								</div>

								{note.editedAt && (
									<div>
										✏️ Edited by <b>{note.editedBy}</b> —{" "}
										{formatDate(note.editedAt)}
									</div>
								)}
							</Box>

							<Box>
								<IconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										handleEdit(note);
									}}
								>
									<Edit fontSize="small" />
								</IconButton>

								<IconButton
									size="small"
									color="error"
									onClick={(e) => {
										e.stopPropagation();
										setOpenDeleteDialog(true);
										selectedNote.current = note;
									}}
								>
									<Delete fontSize="small" />
								</IconButton>
							</Box>
						</Box>
					</Paper>
				))}

				{/* Modal */}
				<Dialog
					open={openModal}
					onClose={() => setOpenModal(false)}
					fullWidth
				>
					<DialogTitle>
						{editNote
							? "Edit Note"
							: `Add Note — ${vehicle.unitNumber}`}
					</DialogTitle>

					<DialogContent sx={{ marginTop: "25px" }}>
						<TextField
							fullWidth
							multiline
							label="Comment"
							value={noteText}
							onChange={(e) => setNoteText(e.target.value)}
							autoFocus
						/>

						{editNote && (
							<Box
								sx={{
									mt: 2,
									fontSize: 12,
									color: "text.secondary",
								}}
							>
								<div>
									<b>Created by:</b> {editNote.createdBy} —{" "}
									{formatDate(editNote.createdAt)}
								</div>

								{editNote.editedBy && (
									<div>
										<b>Last edited by:</b>{" "}
										{editNote.editedBy} —{" "}
										{formatDate(editNote.editedAt)}
									</div>
								)}
							</Box>
						)}
					</DialogContent>

					<DialogActions>
						<Button onClick={() => setOpenModal(false)}>
							Cancel
						</Button>

						<Button
							variant="contained"
							disabled={!noteText.trim()}
							onClick={handleSave}
						>
							{editNote ? "Save Changes" : "Add Note"}
						</Button>
					</DialogActions>
				</Dialog>
			</Box>

			<DeleteConfirmationDialog
				open={openDeleteDialog}
				message="Are you sure you want to delete this vehicle note?"
				onClose={() => setOpenDeleteDialog(false)}
				onConfirm={handleDelete}
			/>
		</>
	);
};

export default NotesTab;
